/**
 * Add-to-cart upsell modal + Upcart-compatible interceptor.
 *
 * Upcart (and similar cart apps) often capture ATC clicks before the theme
 * product-form handler. This file installs a capture-phase listener early so the
 * modal can pause ATC, then resumes by adding items and refreshing Upcart.
 */

const LOG_PREFIX = '[ATC Upsell]';

function log(...args) {
  console.log(LOG_PREFIX, ...args);
}

function warn(...args) {
  console.warn(LOG_PREFIX, ...args);
}

function error(...args) {
  console.error(LOG_PREFIX, ...args);
}

log('Script file evaluating…', {
  readyState: document.readyState,
  href: window.location.href,
});

const CART_ADD_URL =
  (window.Theme && Theme.routes && Theme.routes.cart_add_url) ||
  (window.Shopify && Shopify.routes && Shopify.routes.root
    ? `${Shopify.routes.root}cart/add.js`
    : '/cart/add.js');

/**
 * @returns {HTMLElement & {
 *   enabled?: boolean,
 *   variantId?: string,
 *   requestDecision?: () => Promise<'accept' | 'decline'>,
 *   prompt?: () => Promise<'accept' | 'decline'>
 * } | null}
 */
function getUpsellModal() {
  return document.querySelector('atc-upsell-modal');
}

/**
 * @param {Element | null | undefined} from
 * @returns {HTMLFormElement | null}
 */
function findProductForm(from) {
  if (!(from instanceof Element)) return null;

  const direct = from.closest('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
  if (direct instanceof HTMLFormElement) return direct;

  const host = from.closest('product-form-component');
  const nested = host?.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"], form');
  return nested instanceof HTMLFormElement ? nested : null;
}

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isAddToCartTrigger(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest('.atc-upsell-modal__dialog, atc-upsell-modal')) return false;
  if (target.closest('.quick-add-modal, quick-add-component')) return false;

  const control = target.closest('button, input[type="submit"], input[type="button"]');
  if (!(control instanceof HTMLElement)) return false;
  if (control.hasAttribute('disabled') || control.getAttribute('aria-disabled') === 'true') return false;

  const form = findProductForm(control);
  if (!form) return false;

  if (
    control.matches(
      '[name="add"], [data-add-to-cart], [data-testid="add-to-cart-button"], .add-to-cart-button, add-to-cart-component button, .product-form-buttons button[type="submit"]'
    )
  ) {
    return true;
  }

  return control instanceof HTMLButtonElement && control.type === 'submit';
}

/**
 * @param {Array<{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }>} items
 */
async function addCartItems(items) {
  log('Adding items to cart…', items, 'url=', CART_ADD_URL);
  const response = await fetch(CART_ADD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ items }),
  });

  const data = await response.json();
  log('Cart add response', data);
  if (data.status) {
    throw new Error(data.description || data.message || 'Add to cart failed');
  }
  return data;
}

/**
 * @param {HTMLFormElement} form
 * @returns {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number } | null}
 */
function buildMainItemFromForm(form) {
  const formData = new FormData(form);
  const id = Number(formData.get('id'));
  if (!id) return null;

  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
  const item = {
    id,
    quantity: Number(formData.get('quantity')) || 1,
  };

  /** @type {Record<string, string>} */
  const properties = {};
  formData.forEach((value, key) => {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string') {
      properties[match[1]] = value;
    }
  });
  if (Object.keys(properties).length) item.properties = properties;

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  return item;
}

/**
 * @returns {Array<{ id: number, quantity: number }>}
 */
function getCheckedAccessoryUpsells() {
  /** @type {Array<{ id: number, quantity: number }>} */
  const items = [];
  const seen = new Set();

  document
    .querySelectorAll(
      '.product-upsell-addons input.extraAccessory:checked[data-variant-id], input.extraAccessory:checked[data-variant-id]'
    )
    .forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const id = Number(input.dataset.variantId || input.value);
      if (!id || seen.has(id)) return;
      seen.add(id);
      items.push({ id, quantity: 1 });
    });

  return items;
}

function syncUpcart() {
  log('Syncing Upcart…', {
    hasRefresh: typeof window.upcartRefreshCart === 'function',
    hasOpen: typeof window.upcartOpenCart === 'function',
  });

  try {
    window.upcartRefreshCart?.();
    log('upcartRefreshCart() called');
  } catch (err) {
    warn('upcartRefreshCart failed:', err);
  }

  window.setTimeout(() => {
    try {
      window.upcartOpenCart?.();
      log('upcartOpenCart() called');
    } catch (err) {
      warn('upcartOpenCart failed:', err);
    }
  }, 50);
}

function closeUpcartIfPresent() {
  try {
    if (typeof window.upcartCloseCart === 'function') {
      window.upcartCloseCart();
      log('upcartCloseCart() called');
    }
  } catch (err) {
    warn('upcartCloseCart failed:', err);
  }
}

function bindUpcartGuard() {
  if (window.__atcUpsellUpcartGuardBound) return;
  window.__atcUpsellUpcartGuardBound = true;
  log('Binding Upcart open-guard');

  const guard = () => {
    if (document.documentElement.classList.contains('atc-upsell-modal-open')) {
      log('Upcart tried to open while modal is open — closing Upcart');
      closeUpcartIfPresent();
    }
  };

  const subscribe = () => {
    try {
      if (typeof window.upcartSubscribeCartOpened === 'function') {
        window.upcartSubscribeCartOpened(guard);
        log('Subscribed to upcartSubscribeCartOpened');
      } else {
        log('upcartSubscribeCartOpened not available yet');
      }
    } catch (err) {
      warn('Upcart subscribe failed:', err);
    }
  };

  subscribe();
  window.setTimeout(subscribe, 1000);
  window.setTimeout(subscribe, 3000);
}

function logModalStatus(reason) {
  const modal = getUpsellModal();
  log('Modal status check (' + reason + ')', {
    found: Boolean(modal),
    enabled: modal?.dataset?.enabled,
    disabledReason: modal?.dataset?.disabledReason,
    variantId: modal?.dataset?.variantId,
    upsellProductId: modal?.dataset?.upsellProductId,
    currentProductId: modal?.dataset?.currentProductId,
    missingProduct: modal?.dataset?.missingProduct,
    hasRequestDecision: typeof modal?.requestDecision === 'function',
    isCustomElement: Boolean(customElements.get('atc-upsell-modal')),
    dialogInBody: Boolean(document.body.querySelector('.atc-upsell-modal__dialog')),
  });
}

/**
 * @param {HTMLFormElement} form
 */
async function runUpsellThenAdd(form) {
  log('runUpsellThenAdd() start');
  const modal = getUpsellModal();
  if (!modal) {
    warn('No <atc-upsell-modal> in DOM — abort intercept flow');
    return false;
  }

  try {
    if (!customElements.get('atc-upsell-modal')) {
      log('Waiting for custom element definition…');
      await Promise.race([
        customElements.whenDefined('atc-upsell-modal'),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    }
    customElements.upgrade(modal);
    log('Custom element upgraded');
  } catch (err) {
    warn('Custom element upgrade issue:', err);
  }

  const enabled = modal.dataset.enabled === 'true' && Boolean(modal.dataset.variantId);
  log('Enabled check', {
    enabled,
    dataEnabled: modal.dataset.enabled,
    variantId: modal.dataset.variantId,
  });
  if (!enabled) {
    warn('Modal not enabled / missing variant — letting normal ATC continue');
    return false;
  }

  closeUpcartIfPresent();

  let decision = /** @type {'accept' | 'decline'} */ ('decline');
  try {
    const decisionFn = modal.requestDecision?.bind(modal) || modal.prompt?.bind(modal);
    log('Opening modal for decision…', { hasDecisionFn: typeof decisionFn === 'function' });
    if (typeof decisionFn === 'function') {
      decision = await decisionFn();
    } else {
      warn('No requestDecision/prompt method on modal element');
    }
  } catch (err) {
    error('decision failed:', err);
  }

  log('Shopper decision =', decision);

  const mainItem = buildMainItemFromForm(form);
  if (!mainItem) {
    warn('Main product variant missing from form.');
    return true;
  }

  /** @type {Array<{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }>} */
  const items = [];

  if (decision === 'accept' && modal.dataset.variantId) {
    items.push({ id: Number(modal.dataset.variantId), quantity: 1 });
  }
  items.push(mainItem);
  items.push(...getCheckedAccessoryUpsells());

  const deduped = [];
  const seen = new Set();
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    deduped.push(item);
  }

  log('Final cart payload', deduped);
  await addCartItems(deduped);

  document.querySelectorAll('input.extraAccessory:checked').forEach((checkbox) => {
    if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
  });

  syncUpcart();
  log('runUpsellThenAdd() complete');
  return true;
}

function installAtcUpsellInterceptor() {
  if (window.__atcUpsellInterceptorInstalled) {
    log('Interceptor already installed — skip');
    return;
  }
  window.__atcUpsellInterceptorInstalled = true;

  let busy = false;
  log('Installing capture-phase click interceptor');

  const onClick = (event) => {
    const target = event.target;
    const tag = target instanceof Element ? target.tagName : typeof target;
    log('Document click captured', { tag, busy });

    if (!(event instanceof Event)) return;
    if (busy) {
      log('Ignored click — already busy with modal flow');
      return;
    }

    const isAtc = isAddToCartTrigger(event.target);
    log('Is Add to Cart trigger?', isAtc, {
      targetHTML:
        target instanceof Element ? target.outerHTML.slice(0, 180) : String(target),
    });
    if (!isAtc) return;

    const modal = getUpsellModal();
    logModalStatus('on ATC click');

    if (!modal) {
      warn('ATC clicked but <atc-upsell-modal> not found in DOM');
      return;
    }
    if (modal.dataset.enabled !== 'true') {
      warn('ATC clicked but modal data-enabled is not true:', {
        enabled: modal.dataset.enabled,
        disabledReason: modal.dataset.disabledReason,
        upsellProductId: modal.dataset.upsellProductId,
        upsellProductTitle: modal.dataset.upsellProductTitle,
        currentProductId: modal.dataset.currentProductId,
        variantId: modal.dataset.variantId,
      });
      return;
    }
    if (modal.dataset.sameAsCurrent === 'true') {
      warn(
        'Upsell product is the SAME Shopify product as this page. Modal will still open, but pick a different product in Theme Editor for a real upsell.',
        {
          upsellProductId: modal.dataset.upsellProductId,
          upsellProductTitle: modal.dataset.upsellProductTitle,
          currentProductId: modal.dataset.currentProductId,
        }
      );
    }
    if (!modal.dataset.variantId) {
      warn('ATC clicked but modal has no data-variant-id');
      return;
    }

    const form = findProductForm(/** @type {Element} */ (event.target));
    if (!form) {
      warn('ATC clicked but product form not found');
      return;
    }

    log('Intercepting ATC click — preventDefault + stopImmediatePropagation');
    event.preventDefault();
    event.stopImmediatePropagation();

    busy = true;
    window.__atcUpsellInterceptActive = true;

    runUpsellThenAdd(form)
      .then((handled) => {
        log('Intercept flow finished. handled =', handled);
      })
      .catch((err) => {
        error('Intercepted ATC failed:', err);
      })
      .finally(() => {
        busy = false;
        window.__atcUpsellInterceptActive = false;
        log('Intercept busy flag cleared');
      });
  };

  document.addEventListener('click', onClick, true);
  bindUpcartGuard();
  log('Interceptor installed on document (capture: true)');

  // Delayed status dump after DOM is ready / block may have rendered.
  const dump = () => logModalStatus('post-install dump');
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dump, { once: true });
  } else {
    dump();
  }
  window.setTimeout(() => logModalStatus('1s delayed dump'), 1000);
  window.setTimeout(() => logModalStatus('3s delayed dump'), 3000);
}

class AtcUpsellModal extends HTMLElement {
  /** @type {((value: 'accept' | 'decline') => void) | null} */
  #resolve = null;

  /** @type {number | null} */
  #timerId = null;

  /** @type {number} */
  #remainingSeconds = 0;

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {number} */
  #savedScrollY = 0;

  connectedCallback() {
    log('atc-upsell-modal connectedCallback', {
      enabled: this.dataset.enabled,
      variantId: this.dataset.variantId,
      missingProduct: this.dataset.missingProduct,
    });

    this.#dialog = this.querySelector('dialog');
    this.#backdrop = this.querySelector('[data-atc-upsell-backdrop]');
    log('Dialog found inside host?', Boolean(this.#dialog));

    if (this.#backdrop && this.#backdrop.parentElement !== document.body) {
      document.body.appendChild(this.#backdrop);
    }

    if (this.#dialog && this.#dialog.parentElement !== document.body) {
      document.body.appendChild(this.#dialog);
      log('Dialog moved to document.body');
    }

    this.#bindActions();
    installAtcUpsellInterceptor();
  }

  disconnectedCallback() {
    log('atc-upsell-modal disconnectedCallback');
    this.#stopTimer();
    this.#unbindActions();

    if (this.#backdrop?.isConnected && this.#backdrop.parentElement === document.body) {
      this.#backdrop.remove();
    }

    if (this.#dialog?.isConnected && this.#dialog.parentElement === document.body) {
      this.#dialog.remove();
    }
  }

  get enabled() {
    return this.dataset.enabled === 'true' && Boolean(this.dataset.variantId);
  }

  get variantId() {
    return this.dataset.variantId || '';
  }

  /**
   * Opens the modal and waits for the shopper decision.
   * @returns {Promise<'accept' | 'decline'>}
   */
  requestDecision() {
    log('requestDecision() called', {
      enabled: this.enabled,
      variantId: this.variantId,
    });

    if (!this.enabled) {
      if (this.dataset.missingProduct === 'true') {
        warn('No upsell product selected in theme settings.');
      } else if (!this.dataset.variantId) {
        warn('Upsell product has no available variant.');
      }
      return Promise.resolve(/** @type {'decline'} */ ('decline'));
    }

    if (this.#resolve) {
      log('Previous decision promise still open — resolving as decline');
      this.#resolve('decline');
      this.#resolve = null;
    }

    closeUpcartIfPresent();

    return new Promise((resolve) => {
      this.#resolve = resolve;
      this.#open();
    });
  }

  /** @deprecated Use requestDecision() */
  prompt() {
    return this.requestDecision();
  }

  #bindActions = () => {
    const root = this.#dialog || this;

    root.querySelectorAll('[data-atc-upsell-accept]').forEach((el) => {
      el.addEventListener('click', this.#onAccept);
    });
    root.querySelectorAll('[data-atc-upsell-decline]').forEach((el) => {
      el.addEventListener('click', this.#onDecline);
    });
    root.querySelectorAll('[data-atc-upsell-close]').forEach((el) => {
      el.addEventListener('click', this.#onDecline);
    });

    this.#dialog?.addEventListener('cancel', this.#onCancel);
    this.#backdrop?.addEventListener('click', this.#onBackdropClick);
    document.addEventListener('keydown', this.#onKeydown);
    log('Modal action listeners bound');
  };

  #unbindActions = () => {
    const root = this.#dialog || this;

    root.querySelectorAll('[data-atc-upsell-accept]').forEach((el) => {
      el.removeEventListener('click', this.#onAccept);
    });
    root.querySelectorAll('[data-atc-upsell-decline]').forEach((el) => {
      el.removeEventListener('click', this.#onDecline);
    });
    root.querySelectorAll('[data-atc-upsell-close]').forEach((el) => {
      el.removeEventListener('click', this.#onDecline);
    });

    this.#dialog?.removeEventListener('cancel', this.#onCancel);
    this.#backdrop?.removeEventListener('click', this.#onBackdropClick);
    document.removeEventListener('keydown', this.#onKeydown);
  };

  #onAccept = (event) => {
    event.preventDefault();
    log('Accept clicked');
    this.#finish('accept');
  };

  #onDecline = (event) => {
    event.preventDefault();
    log('Decline/close clicked');
    this.#finish('decline');
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    log('Dialog cancel event');
    this.#finish('decline');
  };

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (!this.#backdrop || event.target !== this.#backdrop) return;
    log('Backdrop clicked');
    this.#finish('decline');
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#dialog?.open) return;
    event.preventDefault();
    log('Escape pressed');
    this.#finish('decline');
  };

  /**
   * @param {'accept' | 'decline'} decision
   */
  #finish(decision) {
    log('Finishing modal with decision =', decision);
    this.#stopTimer();
    this.#close();
    const resolve = this.#resolve;
    this.#resolve = null;
    resolve?.(decision);
  }

  #open() {
    const dialog = this.#dialog || this.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement)) {
      warn('Dialog element missing — cannot open modal');
      this.#finish('decline');
      return;
    }

    this.#dialog = dialog;
    this.#backdrop = this.#backdrop || document.querySelector('[data-atc-upsell-backdrop]');

    try {
      // Non-modal show() keeps document scrolling (normal page scrollbar).
      if (!dialog.open) {
        dialog.show();
      }
      log('dialog.show() success', { open: dialog.open });
    } catch (err) {
      error('dialog.show() failed:', err);
      this.#finish('decline');
      return;
    }

    this.#savedScrollY = window.scrollY || 0;
    if (this.#backdrop) {
      this.#backdrop.hidden = false;
      this.#backdrop.classList.add('is-visible');
    }

    document.documentElement.classList.add('atc-upsell-modal-open');
    document.body.classList.add('atc-upsell-modal-open');
    window.scrollTo(0, 0);
    this.#startTimer();

    const acceptBtn = dialog.querySelector('[data-atc-upsell-accept]');
    if (acceptBtn instanceof HTMLElement) {
      acceptBtn.focus({ preventScroll: true });
    }
  }

  #close() {
    const dialog = this.#dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.close();
      log('dialog.close() called');
    }

    if (this.#backdrop) {
      this.#backdrop.classList.remove('is-visible');
      this.#backdrop.hidden = true;
    }

    document.documentElement.classList.remove('atc-upsell-modal-open');
    document.body.classList.remove('atc-upsell-modal-open');
    window.scrollTo(0, this.#savedScrollY || 0);
  }

  #startTimer() {
    this.#stopTimer();
    const minutes = Number(this.dataset.countdownMinutes) || 15;
    this.#remainingSeconds = Math.max(1, Math.round(minutes * 60));
    this.#renderTimer();
    this.#timerId = window.setInterval(() => {
      this.#remainingSeconds = Math.max(0, this.#remainingSeconds - 1);
      this.#renderTimer();
      if (this.#remainingSeconds <= 0) {
        this.#stopTimer();
      }
    }, 1000);
  }

  #stopTimer() {
    if (this.#timerId != null) {
      clearInterval(this.#timerId);
      this.#timerId = null;
    }
  }

  #renderTimer() {
    const root = this.#dialog || this;
    const el = root.querySelector('[data-atc-upsell-timer]');
    if (!(el instanceof HTMLElement)) return;

    const mins = Math.floor(this.#remainingSeconds / 60);
    const secs = this.#remainingSeconds % 60;
    const time = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    const prefix = this.dataset.countdownPrefix || 'DISCOUNT EXPIRES IN';
    el.textContent = `${prefix} ${time}`;
  }
}

if (!customElements.get('atc-upsell-modal')) {
  customElements.define('atc-upsell-modal', AtcUpsellModal);
  log('Custom element <atc-upsell-modal> defined');
} else {
  log('Custom element <atc-upsell-modal> already defined');
}

installAtcUpsellInterceptor();
log('Bootstrap complete');
